import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** Filters persisted in URL querystring + localStorage per route key. */
export function useFilters(key, defaults = {}) {
  const [params, setParams] = useSearchParams();
  const storageKey = `filters:${key}`;

  const filters = useMemo(() => {
    const stored = (() => {
      try {
        return JSON.parse(localStorage.getItem(storageKey) || "{}");
      } catch {
        return {};
      }
    })();
    const out = { ...defaults, ...stored };
    for (const [k, v] of params.entries()) out[k] = v;
    return out;
  }, [params, storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Set several filters atomically (avoids stale-param overwrites when calling setFilter twice in a row). */
  const setMany = useCallback(
    (patch) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === null || v === "" || v === "all") next.delete(k);
            else next.set(k, String(v));
          }
          const obj = {};
          for (const [kk, vv] of next.entries()) obj[kk] = vv;
          localStorage.setItem(storageKey, JSON.stringify(obj));
          return next;
        },
        { replace: true }
      );
    },
    [setParams, storageKey]
  );

  const setFilter = useCallback((k, v) => setMany({ [k]: v }), [setMany]);

  const reset = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
    localStorage.removeItem(storageKey);
  }, [setParams, storageKey]);

  const active = Object.entries(filters).filter(([k, v]) => v && v !== "all" && defaults[k] !== v).length;
  return { filters, setFilter, setMany, reset, active };
}
